/**
 * PC 考试客户端(二期 · 原型 student/pc-client)的网页这一侧。
 * 客户端程序(clients/exam-pc)在预加载脚本里往 window 上挂 cgExamClient;网页据此知道自己跑在客户端里,
 * 并通过它握手(主进程签名)、给检录 / 进入答题的请求取凭证、进入和解除锁定。普通浏览器里没有这个对象。
 * 协议与防伪造的说明在 apps/api/src/modules/exam/domain/pc-client.ts。
 */
import { ApiError, type ApiClient } from '../api/client.js';

/** 客户端当前状态(主进程推给页面,答题页顶栏的「客户端状态」用) */
export interface ExamClientStatus {
  /** idle 没在考试 · ready 已握手 · locked 锁定中 · degraded 锁定不完整 · exited 口令退出 · released 已解锁 */
  state: 'idle' | 'ready' | 'locked' | 'degraded' | 'exited' | 'released';
  version: string;
  fullscreen: boolean;
  /** 切换键拦截:on 已屏蔽 · off 没开 · failed 没装上(只能记录失焦) */
  keyguard: 'on' | 'off' | 'failed';
  displays: number;
  remote: boolean;
  vm: 'none' | 'suspect' | 'vm';
  /** 累计拦下的切换次数 / 现在命中的禁用程序 / 待上报的事件数 / 最近一次上报成功的时间 */
  blocked: number;
  procs: string[];
  pending: number;
  lastReportAt: string | null;
  online: boolean;
}

/** 握手返回里交给主进程的那部分 */
export interface ClientHandshake {
  required: true;
  serverTime: string;
  ticket: string;
  expiresAt: string;
  deadlineAt: string | null;
  exam: { id: string; name: string; room: string | null };
  policy: Record<string, unknown>;
  exitVerifier: string;
  reportPath: string;
}

export interface ExamClientBridge {
  /** 协议版本(现在是 1)/ 客户端版本 */
  proto: number;
  version: string;
  /** 主进程对一次性随机数签名(带设备指纹与环境) */
  attest(input: { nonce: string; recordId: string }): Promise<{
    version: string;
    device: string;
    env: Record<string, unknown>;
    sig: string;
  }>;
  /** 把握手结果(票、锁定策略、退出口令校验值)交给主进程 */
  begin(input: ClientHandshake & { recordId: string }): Promise<{ ok: boolean }>;
  /** 给检录 / 进入答题的请求现签一个凭证(没握过手返回 null) */
  proof(input: { recordId: string; purpose: 'checkin' | 'enter' }): Promise<string | null>;
  /** 进入锁定(开始答题后)/ 解除(交卷、被交卷、离开答题页) */
  lock(input: { recordId: string; deadlineAt: string | null }): Promise<ExamClientStatus>;
  release(input: { recordId: string; reason: string }): Promise<void>;
  status(): Promise<ExamClientStatus>;
  /** 订阅状态变化;返回取消订阅 */
  onStatus(cb: (s: ExamClientStatus) => void): () => void;
  /** 弹出「输入监考口令退出」的窗口 */
  requestExit(): Promise<void>;
}

declare global {
  interface Window {
    cgExamClient?: ExamClientBridge;
  }
}

export const EXAM_CLIENT_PROTO = 1;

/** 当前页面是不是跑在考试客户端里(协议版本对得上才算) */
export function examClient(): ExamClientBridge | null {
  if (typeof window === 'undefined') return null;
  const c = window.cgExamClient;
  return c && c.proto === EXAM_CLIENT_PROTO ? c : null;
}
export const inExamClient = (): boolean => examClient() !== null;

/**
 * 进检录 / 答题前要不要先带到「请用考试客户端」页:这个考场要求客户端、不在客户端里、监考员也没放行改用浏览器。
 * (服务端照样会拦;这里只是别让考生拍完照才被告知。)
 */
export function needUseClientPage(
  exam: { pcClient?: string | undefined; pcClientPass?: boolean | undefined },
  inClient = inExamClient(),
): boolean {
  return !!exam.pcClient && exam.pcClient !== 'off' && !exam.pcClientPass && !inClient;
}

export interface ClientDownload {
  version: string;
  downloadUrl: string | null;
  sha256: string | null;
  sizeBytes: number | null;
  notes: string | null;
  publishedAt: string | null;
}
/** 「请用考试客户端」页的数据 */
export interface ClientInfo {
  serverTime: string;
  required: boolean;
  mode: string;
  exam: { id: string; name: string };
  room: { id: string; name: string; startAt: string | null; endAt: string | null } | null;
  answerStatus: string;
  clientAvailable: boolean;
  download: ClientDownload | null;
  minimumVersion: string | null;
  browserPass: { at: string; reason: string } | null;
  request: 'none' | 'pending' | 'rejected';
  rejectReason: string | null;
  canSubmitDirect: boolean;
  state: string | null;
}

export function examClientApi(api: ApiClient) {
  const r = (id: string) => `/v1/exam/records/${id}/client`;
  return {
    info: (id: string) => api.get<ClientInfo>(r(id)),
    /** 下载信息(不用登录,机构统一装机也能看) */
    download: () =>
      api.get<{ available: boolean; latest: ClientDownload | null; minimumVersion: string | null }>(
        '/v1/exam/client/download',
      ),
    browserRequest: (id: string, reason?: string) =>
      api.post<{ queued: boolean; passed: boolean }>(`${r(id)}/browser-request`, reason ? { reason } : {}),
    /** 已开考、客户端用不了:按已保存的答案直接交卷 */
    submitDirect: (id: string) =>
      api.post<{ submittedAt: string; unanswered: string[]; judging: number; answerStatus: string }>(
        `${r(id)}/submit`,
      ),
  };
}

/** 握过手的记录(票的过期时间);同一条记录在票过期前不重复握手 */
const sessions = new Map<string, number>();
export function resetClientSession(recordId?: string): void {
  if (recordId) sessions.delete(recordId);
  else sessions.clear();
}

/**
 * 握手:要随机数 → 主进程签名 → 服务端验签发票 → 交给主进程。
 * 返回 ok(握好了)/ not_required(这场不要求客户端,客户端里当普通浏览器用)/ no_client(不在客户端里)。
 * 版本不可用、签名不对抛 ApiError(E0109)。
 */
export async function ensureClientSession(
  api: ApiClient,
  recordId: string,
  bridge: ExamClientBridge | null = examClient(),
  now: () => number = Date.now,
): Promise<'ok' | 'not_required' | 'no_client'> {
  if (!bridge) return 'no_client';
  const exp = sessions.get(recordId);
  // 留 2 分钟余量:快过期的票重新握
  if (exp && exp - now() > 120_000) return 'ok';
  const base = `/v1/exam/records/${recordId}/client`;
  const ch = await api.post<{ nonce: string }>(`${base}/challenge`);
  const att = await bridge.attest({ nonce: ch.nonce, recordId });
  const hs = await api.post<ClientHandshake | { required: false }>(`${base}/handshake`, {
    nonce: ch.nonce,
    version: att.version,
    device: att.device,
    env: att.env,
    sig: att.sig,
  });
  if (!hs.required) return 'not_required';
  await bridge.begin({ ...hs, recordId });
  sessions.set(recordId, new Date(hs.expiresAt).getTime());
  return 'ok';
}

/**
 * 给检录 / 进入答题的请求带上客户端凭证(请求头 x-exam-client)。不在客户端里、或这场不要求客户端 → 不带。
 * 握手失败不在这里抛(返回空):让后面的请求自己被服务端拦下,页面按 E0109 统一处理。
 */
export async function clientProofHeaders(
  api: ApiClient,
  recordId: string,
  purpose: 'checkin' | 'enter',
  bridge: ExamClientBridge | null = examClient(),
): Promise<Record<string, string>> {
  if (!bridge) return {};
  try {
    if ((await ensureClientSession(api, recordId, bridge)) !== 'ok') return {};
    const proof = await bridge.proof({ recordId, purpose });
    return proof ? { 'x-exam-client': proof } : {};
  } catch {
    return {};
  }
}

/** 是不是「须用考试客户端」的拦截(E0109);带出原因:no_client / bad_proof / version */
export function useClientError(e: unknown): { reason: string } | null {
  if (!(e instanceof ApiError) || e.code !== 'E0109') return null;
  const d = (e.data ?? {}) as { reason?: string };
  return { reason: d.reason ?? 'no_client' };
}

/**
 * 带凭证发一次检录 / 进入答题;在客户端里被拦(票过期、服务端重启后换了密钥等)就重新握手再试一次。
 * 浏览器里被拦直接抛给页面(页面带去「请用考试客户端」)。
 */
export async function withClientProof<T>(
  api: ApiClient,
  recordId: string,
  purpose: 'checkin' | 'enter',
  call: (headers: Record<string, string>) => Promise<T>,
  bridge: ExamClientBridge | null = examClient(),
): Promise<T> {
  try {
    return await call(await clientProofHeaders(api, recordId, purpose, bridge));
  } catch (e) {
    if (!bridge || !useClientError(e)) throw e;
    resetClientSession(recordId);
    return call(await clientProofHeaders(api, recordId, purpose, bridge));
  }
}

export const CLIENT_STATE_TEXT: Record<ExamClientStatus['state'], string> = {
  idle: '未进入考试',
  ready: '已握手 · 未锁定',
  locked: '全屏锁定中',
  degraded: '锁定不完整',
  exited: '已用口令退出',
  released: '已解锁',
};

/** 安装包大小 */
export function fmtBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return '';
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}
